// LivePlaybackState reducer (§18). Pure: (previous, event, options) -> next. Updates
// bridge/device/deck/track/playback/beat/loop/tempo state, derives connection quality,
// infers cue jumps from large position deltas, caps the recent-event buffer, and never
// throws on unknown/malformed optional fields.

import type {
  DJSEMLiveEventEnvelope, BridgeHeartbeatPayload, BridgeReadyPayload, BridgeErrorPayload,
  DeviceDiscoveredPayload, DeviceLostPayload, TrackLoadedPayload, TrackUnloadedPayload,
  PlaybackStatePayload, BeatPayload, BarPayload, LoopStatePayload, CueJumpPayload,
  TempoMasterChangedPayload,
} from "./protocol";
import { validateEnvelope } from "./protocol";
import type {
  LivePlaybackState, LivePlaybackReducerOptions, DJSEMLiveDeckState, ConnectionQuality, ConductorMode,
} from "./types";

/**
 * Builds the initial (empty, offline) live playback state.
 * @param now - initial clock timestamp (defaults to 0).
 * @returns a fresh `LivePlaybackState` with no devices/decks and conductor mode "offline".
 */
export function createInitialLivePlaybackState(now = 0): LivePlaybackState {
  return {
    connection: { bridgeConnected: false, bridgeHealthy: false, networkOnline: true, connectionQuality: "offline", warnings: [] },
    devices: {}, decks: {}, liveClock: { now, latencyMs: 0, driftMs: 0 },
    conductorMode: "offline", recentEvents: [], updatedAt: now,
  };
}

const HEARTBEAT_TIMEOUT = 2000;
const RECENT_LIMIT = 50;

function emptyDeck(deckId: string, now: number): DJSEMLiveDeckState {
  return { deckId, status: "unknown", playback: { isPlaying: false, positionSeconds: 0, updatedAt: now }, confidence: 0 };
}

function deriveConnection(s: LivePlaybackState, now: number, timeout: number): { quality: ConnectionQuality; healthy: boolean } {
  if (!s.connection.bridgeConnected) return { quality: "offline", healthy: false };
  if (!s.connection.networkOnline) return { quality: "lost", healthy: false };
  const last = s.connection.lastHeartbeatAt;
  if (last === undefined) return { quality: "good", healthy: true };
  const age = now - last;
  if (age > timeout * 2) return { quality: "lost", healthy: false };
  if (age > timeout) return { quality: "degraded", healthy: false };
  if (s.liveClock.driftMs > 250) return { quality: "degraded", healthy: true };
  return { quality: s.liveClock.driftMs > 80 ? "good" : "excellent", healthy: true };
}

function deriveMode(s: LivePlaybackState): ConductorMode {
  if (!s.connection.bridgeConnected) return "waiting_for_bridge";
  if (!s.connection.bridgeHealthy) return "degraded";
  if (Object.keys(s.devices).length === 0) return "waiting_for_devices";
  const deck = s.activeDeckId ? s.decks[s.activeDeckId] : undefined;
  if (!deck || !deck.track) return "waiting_for_track";
  if (!s.packageMatch || s.packageMatch.status === "not_started") return "matching_package";
  if (s.packageMatch.status === "no_match" || s.packageMatch.status === "unapproved_package") return "safe_mode";
  if (deck.playback.isPlaying) return "conducting";
  return "armed";
}

/**
 * Re-derives connection quality and conductor mode at the current wall-clock time WITHOUT
 * a new event. Call this on an interval so a stalled bridge (no heartbeats) is detected as
 * degraded/lost between events (heartbeat timeout).
 * @param prev - the previous live playback state.
 * @param now - the current wall-clock timestamp.
 * @param options - reducer options (heartbeat timeout override, etc.).
 * @returns the updated state with re-derived connection quality and conductor mode.
 */
export function applyClockTick(prev: LivePlaybackState, now: number, options: LivePlaybackReducerOptions = {}): LivePlaybackState {
  const timeout = options.heartbeatTimeoutMs ?? HEARTBEAT_TIMEOUT;
  const s: LivePlaybackState = { ...prev, connection: { ...prev.connection, warnings: [...prev.connection.warnings] }, liveClock: { ...prev.liveClock, now }, updatedAt: now };
  const conn = deriveConnection(s, now, timeout);
  s.connection.connectionQuality = conn.quality;
  s.connection.bridgeHealthy = conn.healthy;
  s.conductorMode = deriveMode(s);
  return s;
}

/**
 * Pure reducer: applies one normalized live event envelope to the previous playback state,
 * updating bridge/device/deck/track/playback/beat/loop/tempo state, deriving connection
 * quality, inferring cue jumps from large position deltas, and capping the recent-event
 * buffer. Malformed envelopes are ignored (the previous state is returned unchanged) and
 * unknown event types are tolerated rather than throwing.
 * @param prev - the previous live playback state.
 * @param env - the incoming normalized live event envelope.
 * @param options - reducer options (clock override, heartbeat timeout, buffer limits, thresholds).
 * @returns the next live playback state.
 */
export function reduceLivePlaybackState(
  prev: LivePlaybackState, env: DJSEMLiveEventEnvelope, options: LivePlaybackReducerOptions = {},
): LivePlaybackState {
  const check = validateEnvelope(env);
  if (!check.valid) return prev; // malformed -> ignore safely
  const now = options.now ?? env.timestamp;
  const timeout = options.heartbeatTimeoutMs ?? HEARTBEAT_TIMEOUT;
  const limit = options.recentEventLimit ?? RECENT_LIMIT;
  const jumpThreshold = options.cueJumpDetectionThresholdSeconds ?? 1.0;

  const s: LivePlaybackState = {
    ...prev,
    connection: { ...prev.connection, warnings: [...prev.connection.warnings] },
    devices: { ...prev.devices }, decks: { ...prev.decks },
    liveClock: { ...prev.liveClock, now },
    recentEvents: [env, ...prev.recentEvents].slice(0, limit),
    updatedAt: now,
  };

  const p = env.payload as Record<string, unknown>;
  const deckId = typeof p?.deckId === "string" ? p.deckId : undefined;
  const ensureDeck = (id: string) => { if (!s.decks[id]) s.decks[id] = emptyDeck(id, now); else s.decks[id] = { ...s.decks[id] }; return s.decks[id]; };

  switch (env.type) {
    case "BRIDGE_READY": {
      const _ = env.payload as BridgeReadyPayload; void _;
      s.connection.bridgeConnected = true; s.connection.bridgeHealthy = true; s.connection.lastHeartbeatAt = now;
      break;
    }
    case "BRIDGE_HEARTBEAT": {
      const h = env.payload as BridgeHeartbeatPayload;
      s.connection.bridgeConnected = true; s.connection.lastHeartbeatAt = now;
      if (h?.activeDeckId) s.activeDeckId = h.activeDeckId;
      if (h?.tempoMasterDeckId) s.tempoMasterDeckId = h.tempoMasterDeckId;
      s.connection.warnings = Array.isArray(h?.warnings) ? h.warnings : s.connection.warnings;
      break;
    }
    case "BRIDGE_ERROR": {
      const e = env.payload as BridgeErrorPayload;
      if (e?.severity === "fatal") { s.connection.bridgeConnected = false; s.connection.bridgeHealthy = false; }
      if (e?.message) s.connection.warnings = [...s.connection.warnings, e.message].slice(-10);
      break;
    }
    case "DEVICE_DISCOVERED": {
      const d = env.payload as DeviceDiscoveredPayload;
      if (d?.deviceId) s.devices[d.deviceId] = { deviceId: d.deviceId, playerNumber: d.playerNumber, name: d.name, ipAddress: d.ipAddress, deviceType: d.deviceType ?? "unknown", status: "online", lastSeenAt: now, confidence: 1 };
      break;
    }
    case "DEVICE_LOST": {
      const d = env.payload as DeviceLostPayload;
      if (d?.deviceId && s.devices[d.deviceId]) s.devices[d.deviceId] = { ...s.devices[d.deviceId], status: "lost", lastSeenAt: now };
      break;
    }
    case "DECK_DISCOVERED": { if (deckId) ensureDeck(deckId).status = "empty"; break; }
    case "DECK_LOST": { if (deckId && s.decks[deckId]) { s.decks[deckId] = { ...s.decks[deckId], status: "lost" }; if (s.activeDeckId === deckId) s.activeDeckId = undefined; } break; }
    case "TRACK_LOADED": {
      const t = env.payload as TrackLoadedPayload;
      if (deckId) {
        const deck = ensureDeck(deckId);
        deck.track = { liveTrackId: t.trackIdentity?.liveTrackId, rekordboxId: t.trackIdentity?.rekordboxId, title: t.trackIdentity?.title, artist: t.trackIdentity?.artist, remixName: t.trackIdentity?.remixName, sourceFilename: t.trackIdentity?.sourceFilename, durationSeconds: t.trackIdentity?.durationSeconds, bpm: t.trackIdentity?.bpm, key: t.trackIdentity?.key, loadedAt: now, confidence: t.confidence ?? 0.5 };
        deck.status = "loaded";
        s.activeDeckId = s.activeDeckId ?? deckId;
        if (s.activeDeckId === deckId) { s.activeTrack = deck.track; s.packageMatch = { status: "not_started", confidence: 0, candidates: [] }; }
      }
      break;
    }
    case "TRACK_UNLOADED": {
      const t = env.payload as TrackUnloadedPayload;
      if (t?.deckId && s.decks[t.deckId]) { s.decks[t.deckId] = { ...s.decks[t.deckId], track: undefined, status: "empty" }; if (s.activeDeckId === t.deckId) { s.activeTrack = undefined; s.packageMatch = undefined; s.activeShowCue = undefined; } }
      break;
    }
    case "PLAYBACK_STATE": {
      const pb = env.payload as PlaybackStatePayload;
      if (deckId) {
        const deck = ensureDeck(deckId);
        const prevPos = deck.playback.positionSeconds;
        deck.playback = { isPlaying: !!pb.isPlaying, positionSeconds: pb.positionSeconds ?? prevPos, positionBeats: pb.positionBeats, barNumber: pb.barNumber, beatInBar: pb.beatInBar, bpm: pb.bpm, effectiveBpm: pb.effectiveBpm, pitchPercent: pb.pitchPercent, tempoMaster: pb.tempoMaster, synced: pb.synced, updatedAt: now };
        deck.status = pb.isPlaying ? "playing" : (pb.isCued ? "cued" : "paused");
        if (pb.tempoMaster) s.tempoMasterDeckId = deckId;
        // infer a cue jump if position moved far without a CUE_JUMP event
        if (s.activeDeckId === deckId && Math.abs((pb.positionSeconds ?? prevPos) - prevPos) >= jumpThreshold && deck.playback.isPlaying) {
          s.connection.warnings = [...s.connection.warnings, `inferred seek on ${deckId}`].slice(-10);
        }
        if (s.activeDeckId === deckId) s.liveClock.estimatedPositionSeconds = pb.positionSeconds;
      }
      break;
    }
    case "BEAT": {
      const b = env.payload as BeatPayload;
      s.liveClock.lastBeatAt = now;
      if (deckId) { const deck = ensureDeck(deckId); deck.playback = { ...deck.playback, barNumber: b.barNumber ?? deck.playback.barNumber, beatInBar: b.beatInBar ?? deck.playback.beatInBar, bpm: b.bpm ?? deck.playback.bpm, effectiveBpm: b.effectiveBpm ?? deck.playback.effectiveBpm, updatedAt: now }; if (b.isTempoMaster) s.tempoMasterDeckId = deckId; }
      break;
    }
    case "BAR": { const b = env.payload as BarPayload; if (deckId) { const deck = ensureDeck(deckId); deck.playback = { ...deck.playback, barNumber: b.barNumber ?? deck.playback.barNumber, updatedAt: now }; } break; }
    case "LOOP_STATE": { const l = env.payload as LoopStatePayload; if (deckId) { const deck = ensureDeck(deckId); deck.loop = { isLooping: !!l.isLooping, loopStartSeconds: l.loopStartSeconds, loopEndSeconds: l.loopEndSeconds, loopLengthBeats: l.loopLengthBeats }; } break; }
    case "CUE_JUMP": case "SEEK": {
      const c = env.payload as CueJumpPayload;
      if (deckId) { const deck = ensureDeck(deckId); deck.playback = { ...deck.playback, positionSeconds: c.toSeconds ?? deck.playback.positionSeconds, updatedAt: now }; if (s.activeDeckId === deckId) s.liveClock.estimatedPositionSeconds = c.toSeconds; }
      break;
    }
    case "TEMPO_MASTER_CHANGED": { const t = env.payload as TempoMasterChangedPayload; if (t?.newDeckId) s.tempoMasterDeckId = t.newDeckId; break; }
    default: break; // DEVICE_STATUS / PHRASE_POSITION / PITCH / SYNC / MEDIA / CONNECTION_QUALITY / SIMULATED / unknown -> tolerated
  }

  if (s.activeDeckId) s.activeTrack = s.decks[s.activeDeckId]?.track ?? s.activeTrack;
  const conn = deriveConnection(s, now, timeout);
  s.connection.connectionQuality = conn.quality; s.connection.bridgeHealthy = conn.healthy;
  s.conductorMode = deriveMode(s);
  return s;
}
